/**
 * Men's Health Intake Template
 * 
 * TRT/Testosterone therapy and male peptide protocols
 * Includes ADAM Questionnaire and STOP-Bang screening
 */

import { IntakeTemplate, IntakeSection, IntakeQuestion } from './types';

// ============================================================================
// ADAM Questionnaire Questions
// ============================================================================

const ADAM_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'adam_q1_decreased_libido',
    text: 'Do you have a decrease in libido (sex drive)?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q2_lack_of_energy',
    text: 'Do you have a lack of energy?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q3_decreased_strength',
    text: 'Do you have a decrease in strength and/or endurance?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q4_lost_height',
    text: 'Have you lost height?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q5_decreased_enjoyment',
    text: 'Have you noticed a decreased enjoyment of life?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q6_sad_or_grumpy',
    text: 'Are you sad and/or grumpy?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q7_weaker_erections',
    text: 'Are your erections less strong?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q8_sports_performance_decline',
    text: 'Have you noticed a recent deterioration in your ability to play sports?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q9_falling_asleep_after_dinner',
    text: 'Are you falling asleep after dinner?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  },
  {
    id: 'adam_q10_work_performance_decline',
    text: 'Has there been a recent deterioration in your work performance?',
    inputType: 'boolean',
    required: true,
    category: 'adam'
  }
];

// ============================================================================
// STOP-Bang Questionnaire Questions
// ============================================================================

const STOP_BANG_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'stopbang_snore',
    text: 'Do you SNORE loudly (louder than talking or loud enough to be heard through closed doors)?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  },
  {
    id: 'stopbang_tired',
    text: 'Do you often feel TIRED, fatigued, or sleepy during daytime?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  },
  {
    id: 'stopbang_observed',
    text: 'Has anyone OBSERVED you stop breathing during your sleep?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  },
  {
    id: 'stopbang_pressure',
    text: 'Do you have or are you being treated for high blood PRESSURE?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  },
  {
    id: 'stopbang_bmi',
    text: 'Is your BMI more than 35 kg/m²?',
    inputType: 'boolean',
    required: true,
    helpText: 'BMI over 35 is considered obese',
    category: 'stop_bang'
  },
  {
    id: 'stopbang_age',
    text: 'Are you over 50 years old?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  },
  {
    id: 'stopbang_neck',
    text: 'Is your neck circumference greater than 40 cm (15.75 inches)?',
    inputType: 'boolean',
    required: true,
    helpText: 'Measured around the Adams apple',
    category: 'stop_bang'
  },
  {
    id: 'stopbang_male',
    text: 'Is your biological sex male?',
    inputType: 'boolean',
    required: true,
    category: 'stop_bang'
  }
];

// ============================================================================
// Sections
// ============================================================================

const ADAM_SECTION: IntakeSection = {
  id: 'adam_questionnaire',
  title: 'ADAM Questionnaire',
  description: 'Androgen Deficiency in Aging Males - This validated questionnaire helps assess symptoms of low testosterone.',
  icon: 'activity',
  questions: ADAM_QUESTIONS
};

const STOP_BANG_SECTION: IntakeSection = {
  id: 'stop_bang_questionnaire',
  title: 'STOP-Bang Screening',
  description: 'Sleep apnea screening - Important for TRT safety assessment.',
  icon: 'moon',
  questions: STOP_BANG_QUESTIONS
};

const MEDICAL_HISTORY_SECTION: IntakeSection = {
  id: 'medical_history',
  title: 'Medical History',
  description: 'Important medical information for safe prescribing.',
  icon: 'clipboard',
  questions: [
    {
      id: 'prostate_cancer_history',
      text: 'Have you ever been diagnosed with prostate cancer?',
      inputType: 'boolean',
      required: true,
      helpText: 'This is critical for TRT safety'
    },
    {
      id: 'cardiovascular_disease',
      text: 'Do you have any history of heart disease, heart attack, or stroke?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'cardiovascular_details',
      text: 'If yes, please provide details:',
      inputType: 'text',
      conditionalOn: {
        questionId: 'cardiovascular_disease',
        operator: 'equals',
        value: true
      }
    },
    {
      id: 'blood_clots',
      text: 'Have you ever had blood clots (DVT, PE)?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'liver_disease',
      text: 'Do you have any liver disease or elevated liver enzymes?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'taking_warfarin',
      text: 'Are you currently taking warfarin (Coumadin) or other blood thinners?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'fertility_concerns',
      text: 'Are you planning to father children in the near future?',
      inputType: 'boolean',
      required: true,
      helpText: 'TRT can affect fertility - alternatives may be recommended'
    },
    {
      id: 'previous_trt',
      text: 'Have you used testosterone therapy before?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'previous_trt_details',
      text: 'If yes, please describe your experience:',
      inputType: 'text',
      conditionalOn: {
        questionId: 'previous_trt',
        operator: 'equals',
        value: true
      }
    },
    {
      id: 'current_medications',
      text: 'Select all current medications:',
      inputType: 'multiselect',
      options: [
        'Levothyroxine', 'Liothyronine', 'Armour Thyroid',
        'Lisinopril', 'Amlodipine', 'Losartan', 'Metoprolol', 'Hydrochlorothiazide',
        'Metformin', 'Insulin', 'Glipizide', 'Empagliflozin',
        'Atorvastatin', 'Rosuvastatin', 'Simvastatin',
        'Sertraline', 'Fluoxetine', 'Escitalopram', 'Bupropion', 'Trazodone',
        'Testosterone', 'Clomiphene', 'Anastrozole', 'HCG',
        'Ibuprofen', 'Naproxen', 'Acetaminophen', 'Gabapentin', 'Prednisone',
        'Omeprazole', 'Pantoprazole', 'Montelukast', 'Albuterol',
        'Vitamin D', 'Vitamin B12', 'Iron supplement', 'Calcium', 'Magnesium', 'Multivitamin', 'Fish oil / Omega-3', 'Probiotics',
        'Finasteride', 'Dutasteride', 'Tamsulosin', 'Sildenafil', 'Tadalafil',
      ],
      required: true,
      helpText: 'Select all that apply, including prescriptions, OTC, and supplements.'
    },
    {
      id: 'allergies',
      text: 'Do you have any drug allergies?',
      inputType: 'text',
      required: true
    }
  ]
};

const LIFESTYLE_SECTION: IntakeSection = {
  id: 'lifestyle',
  title: 'Lifestyle Assessment',
  description: 'Information about your daily habits and activity level.',
  icon: 'user',
  questions: [
    {
      id: 'activity_level',
      text: 'How would you describe your activity level?',
      inputType: 'select',
      options: ['Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active', 'Extremely Active'],
      required: true
    },
    {
      id: 'alcohol_use',
      text: 'How many alcoholic drinks do you consume per week?',
      inputType: 'select',
      options: ['None', '1-3', '4-7', '8-14', '15+'],
      required: true
    },
    {
      id: 'tobacco_use',
      text: 'Do you use tobacco products?',
      inputType: 'select',
      options: ['Never', 'Former user', 'Current user - occasionally', 'Current user - daily'],
      required: true
    },
    {
      id: 'athlete_status',
      text: 'Are you a competitive athlete subject to drug testing (WADA/USADA)?',
      inputType: 'boolean',
      required: true,
      helpText: 'Important: Some peptides are prohibited in competition'
    },
    {
      id: 'competitive_sport',
      text: 'If yes, what sport(s)?',
      inputType: 'text',
      conditionalOn: {
        questionId: 'athlete_status',
        operator: 'equals',
        value: true
      }
    }
  ]
};

const SHIPPING_BILLING_SECTION: IntakeSection = {
  id: 'shipping_billing_address',
  title: 'Shipping & Billing Address',
  description: 'Where should we ship your medications and send billing?',
  icon: 'package',
  questions: [
    {
      id: 'shipping_address_line1',
      text: 'Shipping Street Address',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_address_line2',
      text: 'Shipping Apt / Suite / Unit',
      inputType: 'text',
    },
    {
      id: 'shipping_city',
      text: 'Shipping City',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_state',
      text: 'Shipping State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      required: true,
    },
    {
      id: 'shipping_postal_code',
      text: 'Shipping ZIP Code',
      inputType: 'text',
      required: true,
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
    {
      id: 'billing_same_as_shipping',
      text: 'Is your billing address the same as your shipping address?',
      inputType: 'boolean',
      required: true,
    },
    {
      id: 'billing_address_line1',
      text: 'Billing Street Address',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_address_line2',
      text: 'Billing Apt / Suite / Unit',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_city',
      text: 'Billing City',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_state',
      text: 'Billing State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_postal_code',
      text: 'Billing ZIP Code',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
  ]
};

const GOALS_SECTION: IntakeSection = {
  id: 'goals',
  title: 'Treatment Goals',
  description: 'What are you hoping to achieve with treatment?',
  icon: 'target',
  questions: [
    {
      id: 'treatment_goals',
      text: 'What are your primary treatment goals?',
      inputType: 'multiselect',
      options: [
        'Improve energy levels',
        'Increase muscle mass/strength',
        'Improve libido/sexual function',
        'Better mood/mental clarity',
        'Weight loss',
        'Improve sleep',
        'Overall wellness/optimization'
      ],
      required: true
    },
    {
      id: 'additional_concerns',
      text: 'Any additional concerns or questions?',
      inputType: 'text'
    }
  ]
};

// ============================================================================
// Complete Template
// ============================================================================

export const MENS_HEALTH_TEMPLATE: IntakeTemplate = {
  id: 'mens_health_intake',
  templateType: 'mens_health',
  name: "Men's Health Intake (TRT, Peptides)",
  description: 'Comprehensive intake for testosterone replacement therapy and male peptide protocols. Includes ADAM questionnaire for symptom assessment and STOP-Bang for sleep apnea screening.',
  estimatedTime: '10-15 minutes',
  sections: [
    ADAM_SECTION,
    STOP_BANG_SECTION,
    MEDICAL_HISTORY_SECTION,
    LIFESTYLE_SECTION,
    SHIPPING_BILLING_SECTION,
    GOALS_SECTION
  ],
  scoringLogic: {
    type: 'threshold',
    questionsToScore: ADAM_QUESTIONS.map(q => q.id),
    thresholds: {
      low: 3,
      medium: 4,
      high: 7
    },
    interpretation: {
      low: 'Score below 4 suggests lower probability of hypogonadism',
      medium: 'Score of 4+ suggests clinically relevant low testosterone symptoms',
      high: 'High score - strongly consider hormone evaluation'
    }
  }
};

/**
 * Calculate ADAM score from responses
 */
export function calculateADAMScore(responses: Record<string, boolean>): {
  score: number;
  clinicallySignificant: boolean;
  interpretation: string;
} {
  const yesCount = ADAM_QUESTIONS.filter(q => responses[q.id] === true).length;
  const clinicallySignificant = yesCount >= 4;
  
  return {
    score: yesCount,
    clinicallySignificant,
    interpretation: clinicallySignificant
      ? 'Score of 4 or more suggests clinically relevant low testosterone symptoms'
      : 'Score below 4 suggests lower probability of hypogonadism'
  };
}

/**
 * Calculate STOP-Bang score from responses
 */
export function calculateStopBangScore(responses: Record<string, boolean>): {
  score: number;
  riskCategory: 'low' | 'intermediate' | 'high';
  interpretation: string;
  requiresSleepStudy: boolean;
} {
  const yesCount = STOP_BANG_QUESTIONS.filter(q => responses[q.id] === true).length;
  
  let riskCategory: 'low' | 'intermediate' | 'high';
  let interpretation: string;
  let requiresSleepStudy: boolean;

  if (yesCount <= 2) {
    riskCategory = 'low';
    interpretation = 'Low risk for obstructive sleep apnea';
    requiresSleepStudy = false;
  } else if (yesCount <= 4) {
    riskCategory = 'intermediate';
    interpretation = 'Intermediate risk for obstructive sleep apnea';
    requiresSleepStudy = false;
  } else {
    riskCategory = 'high';
    interpretation = 'High risk for obstructive sleep apnea - sleep study recommended before TRT';
    requiresSleepStudy = true;
  }

  return {
    score: yesCount,
    riskCategory,
    interpretation,
    requiresSleepStudy
  };
}

export default MENS_HEALTH_TEMPLATE;
